import type { CartesianPose } from '@sb/geometry';
import { distanceBetweenPoses } from '@sb/geometry';
import { makeNamespacedLog } from '@sb/log';
import type { ArmTarget } from '@sb/motion-planning';
import type { RoutineContext } from '@sb/routine-runner/RoutineContext';
import type { StatefulEventEmitter } from '@sb/utilities';

const log = makeNamespacedLog('WaypointReachedTracker');

// Distance threshold to determine if a waypoint has been reached
const RELAXED_THRESHOLD_DIST = 0.1; // 10cm

const STRICT_THRESHOLD_DIST = 0.002; // 2mm

// Interval to check if a waypoint has been reached
const WAYPOINT_REACHED_CHECK_INTERVAL_MS = 100;

/**
 * This class is a helper class for RoutineContext to track if a waypoint has
 * been reached based on the current pose and the target list.  This is to solve
 * the issue when we pause and resume during a motion with multiple waypoints.
 * This class is separated to keep the logic separate from the RoutineContext class.
 *
 * This class works by checking the current pose against first target in the list.
 * Once the distance is below a threshold, the target is considered visited, and
 * the next target is checked.  This continues until all targets are visited.
 */
export class WaypointReachedTracker {
  private targets: ArmTarget[];

  private events: StatefulEventEmitter<any>;

  private routineContext: RoutineContext;

  private nextTargetIndex: number = 0;

  private nextTargetPose: CartesianPose | null = null;

  private nextTargetPoseDistThreshold: number = RELAXED_THRESHOLD_DIST;

  private cancelPauseListener: () => void;

  private cancelBeginMotionListener: () => void;

  private checkWaypointReachedInterval: ReturnType<typeof setInterval> | null =
    null;

  // use index as key to avoid recomputing the pose for the same target
  private targetPoseCache: Map<number, CartesianPose> = new Map();

  private previousDist: number = Infinity;

  // Private constructor, use WaypointReachedTracker.create instead
  private constructor({
    targets,
    events,
    routineContext,
  }: {
    targets: ArmTarget[];
    events: StatefulEventEmitter<any>;
    routineContext: RoutineContext;
  }) {
    this.targets = targets;
    this.events = events;
    this.routineContext = routineContext;

    // Store cancel functions for each listener so we can cancel them later
    this.cancelPauseListener = this.events.on('pause', () => {
      this.clearCheckWaypointReachedInterval();
    });

    this.cancelBeginMotionListener = this.events.on('beginMotion', () => {
      this.setCheckWaypointReachedInterval();
    });
  }

  // Async static factory to ensure tracker is initialized before returning
  public static async create(params: {
    targets: ArmTarget[];
    events: StatefulEventEmitter<any>;
    routineContext: RoutineContext;
  }): Promise<WaypointReachedTracker> {
    const tracker = new WaypointReachedTracker(params);
    await tracker.init();

    return tracker;
  }

  private async init() {
    try {
      await this.updateNextTargetPose();
    } catch (error) {
      log.error(
        'init.failed',
        'Failed to initialize WaypointReachedTracker:',
        error,
      );

      throw error;
    }
  }

  public destroy() {
    this.clearCheckWaypointReachedInterval();
    this.cancelPauseListener();
    this.cancelBeginMotionListener();
  }

  /**
   * Sets interval to call the checkWaypointReached function
   */
  public setCheckWaypointReachedInterval() {
    if (this.nextTargetIndex >= this.targets.length) {
      log.info(
        'setCheckWaypointReachedInterval',
        'All targets reached, not setting check waypoint reached interval',
      );

      return;
    }

    log.info(
      'setCheckWaypointReachedInterval',
      `Setting check waypoint reached for ${this.targets.length} points with interval ${WAYPOINT_REACHED_CHECK_INTERVAL_MS}ms`,
    );

    this.clearCheckWaypointReachedInterval();

    // if we are using waypoints, run the check to see if we have reached a waypoint
    this.checkWaypointReachedInterval = setInterval(async () => {
      await this.checkWaypointReached();
    }, WAYPOINT_REACHED_CHECK_INTERVAL_MS);
  }

  /**
   * Clears the checkWaypointReached interval
   */
  public clearCheckWaypointReachedInterval() {
    if (!this.checkWaypointReachedInterval) {
      return;
    }

    log.info(
      'clearCheckWaypointReachedInterval',
      'Clearing check waypoint reached interval',
    );

    clearInterval(this.checkWaypointReachedInterval);
    this.checkWaypointReachedInterval = null;
  }

  public isChecking(): boolean {
    return this.checkWaypointReachedInterval !== null;
  }

  /**
   * Return the list of targets that have not been reached yet.  We do simplier
   * approach of just return the targets from our target list, instead
   * of actually comparing the passed target list to the our own target list.
   * So this could possibly break if getUpdatedTargets is using a different list
   */
  public getUpdatedTargets(_: ArmTarget[]): ArmTarget[] {
    // This has been changes to allow for empty list returns for updated targets.
    // This allows full traversal of a single guided mode motion.
    return this.targets.slice(this.nextTargetIndex);
  }

  /**
   * Check if all waypoints have been completed.
   */
  public get allWaypointsCompleted(): boolean {
    return this.nextTargetIndex >= this.targets.length;
  }

  private getCurrentWristPose() {
    return this.routineContext.getRoutineRunnerState().kinematicState.wristPose;
  }

  private async getTargetPose(index: number): Promise<CartesianPose> {
    // Cache the target poses for this tracker instance
    if (!this.targetPoseCache.has(index)) {
      this.targetPoseCache.set(
        index,
        await this.routineContext.getTargetPose(this.targets[index]),
      );
    }

    return this.targetPoseCache.get(index)!;
  }

  /**
   * Check to see if we have reached the next waypoint.
   */
  private async checkWaypointReached() {
    if (this.nextTargetIndex >= this.targets.length) {
      this.clearCheckWaypointReachedInterval();

      return;
    }

    if (this.nextTargetPose == null) {
      // All waypoints have been completed, stop checking
      this.clearCheckWaypointReachedInterval();

      return;
    }

    // We operate on wrist poses only because the target poses get converted to wrist poses
    // by parent RoutineContext.doMotionWithRos
    const currentPose = this.getCurrentWristPose();

    const dist = distanceBetweenPoses(
      currentPose,
      this.routineContext.addEquipmentOffset(
        this.nextTargetPose!, // this.nextTargetPose is not null because we check for it indirectly at beginning of checkWaypointReached
      ).wristPose,
    );

    // we consider the waypoint reached if the distance is less than the threshold
    // AND the arm is moving away from the waypoint
    // OR the distance is less than 2mm
    if (
      (dist <= this.nextTargetPoseDistThreshold && dist >= this.previousDist) ||
      dist < STRICT_THRESHOLD_DIST
    ) {
      // remove the first target. When the first routine is resumed, it will
      // replay from the next target, since we have removed the first one

      this.events.emit('waypointReached', this.nextTargetIndex);

      this.nextTargetIndex += 1;
      await this.updateNextTargetPose();
      this.previousDist = Infinity;
    }

    this.previousDist = dist;
  }

  private async getClampedBlendRadius(
    index: number,
    pose: CartesianPose,
  ): Promise<number> {
    // This function clamps the blend radius to be the min of distAB / 2 and distBC / 2
    // where A, B, C are the targets at index - 1, index, index + 1. This matches the logic
    // in r2cb.
    // standard_bots_cartesian_planning/include/standard_bots_cartesian_planning/segments/hybrid_blending.hpp#L706
    const targetB = this.targets[index];
    const poseB = pose; // pose for the this.targets[index]; this avoids recomputing the pose
    const blendRadius = targetB.blendRadius ?? 0;

    // For single targets just return the blend radius
    if (index === 0 && this.targets.length === 1) {
      return blendRadius;
    }

    // don't need to check dist AB for first target
    if (index === 0) {
      const poseC = await this.getTargetPose(index + 1);
      const distBC = distanceBetweenPoses(poseB, poseC);

      return Math.min(distBC / 2, blendRadius);
    }

    // don't need to check dist BC for last target
    if (index === this.targets.length - 1) {
      const poseA = await this.getTargetPose(index - 1);

      const distAB = distanceBetweenPoses(poseA, poseB);

      return Math.min(distAB / 2, blendRadius);
    }

    // for non-first or non-last targets, we need to compute poses for A, B, C to calculate
    // the min of distAB / 2 and distBC / 2
    const [poseA, poseC] = await Promise.all([
      this.getTargetPose(index - 1),
      this.getTargetPose(index + 1),
    ]);

    const distAB = distanceBetweenPoses(poseA, poseB);
    const distBC = distanceBetweenPoses(poseB, poseC);
    const maxRadius = Math.min(distAB, distBC) / 2;

    return Math.min(maxRadius, blendRadius);
  }

  private async updateNextTargetPose() {
    if (this.nextTargetIndex < this.targets.length) {
      try {
        this.nextTargetPose = await this.getTargetPose(this.nextTargetIndex);

        const blendRadius = await this.getClampedBlendRadius(
          this.nextTargetIndex,
          this.nextTargetPose,
        );

        this.nextTargetPoseDistThreshold = Math.max(
          blendRadius,
          RELAXED_THRESHOLD_DIST,
        );

        log.info(
          'updateNextTargetPose',
          `Updated next waypoint index to ${this.nextTargetIndex} of ${this.targets.length - 1} with dist threshold ${this.nextTargetPoseDistThreshold.toFixed(4)}m`,
          {
            clampedBlendRadius: blendRadius,
          },
        );
      } catch (error) {
        log.error(
          'updateNextTargetPose.failed',
          'Failed to update next target pose',
          error,
        );
      }
    } else {
      // All waypoints have been completed
      this.nextTargetPose = null;
      this.nextTargetPoseDistThreshold = Infinity;
    }
  }
}
